import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FlameIcon } from "lucide-react";
import { format, subDays } from "date-fns";
import { useLearner } from "../../contexts/LearnerContext";
import { useCountUp } from "../../hooks/useCountUp";
import { Card } from "../ui/Card";
import { spring } from "../../utils/motion";
export function TodayPanel() {
  const {
    xpToday,
    dailyGoal,
    streak,
    courses
  } = useLearner();
  const reduce = useReducedMotion() ?? false;
  const pct = Math.min(xpToday / dailyGoal, 1);
  const {
    ref,
    value
  } = useCountUp(streak);
  const today = new Date();
  const week = Array.from({
    length: 7
  }, (_, i) => {
    const xp = courses.reduce((sum, c) => sum + (c.weekXp[i] ?? 0), 0);
    return {
      label: format(subDays(today, 6 - i), "EEEEE"),
      active: xp > 0 || i === 6 && xpToday > 0
    };
  });
  const r = 34;
  const circ = 2 * Math.PI * r;
  return <Card as="section" labelledBy="today-title" className="p-5" data-id="element-239" data-mp="3uzq0th51s79l">
      <h2 id="today-title" className="text-sm font-bold text-ink-2" data-id="element-240" data-mp="3v2c9jk3ocste">
        Today
      </h2>
      <div className="mt-3 flex items-center gap-5" data-id="element-241" data-mp="2yy8yw8wrqhxx">
        <div className="relative h-[84px] w-[84px] shrink-0" data-id="element-242" data-mp="33rtww0yd3bx4">
          <svg viewBox="0 0 84 84" className="h-full w-full -rotate-90" aria-hidden="true" data-id="element-243" data-mp="2pznj2liolh0u">
            <circle cx="42" cy="42" r={r} fill="none" strokeWidth="10" className="stroke-sunken" data-id="element-244" data-mp="2uyi13l8tr1jw" />
            <circle cx="42" cy="42" r={r} fill="none" strokeWidth="10" className="stroke-line" strokeOpacity="0.15" data-id="element-245" data-mp="3io2vd0afhl8h" />
            <motion.circle cx="42" cy="42" r={r} fill="none" strokeWidth="10" strokeLinecap="round" className="stroke-brand" strokeDasharray={circ} initial={reduce ? false : {
            strokeDashoffset: circ
          }} animate={{
            strokeDashoffset: circ * (1 - pct)
          }} transition={{
            ...spring.smooth,
            delay: 0.15
          }} data-id="element-246" data-mp="3mq68dtcqwxvv" />
          </svg>
          <div className="absolute inset-0 grid place-items-center text-center" data-id="element-247" data-mp="1i7ov8jjd71u3">
            <span className="text-lg font-bold leading-none text-ink" data-id="element-248" data-mp="1i9t1v98neey2">
              {xpToday}
              <span className="block text-[10px] font-semibold text-ink-2" data-id="element-249" data-mp="1e87olms7bync">/ {dailyGoal} XP</span>
            </span>
          </div>
        </div>
        <div className="min-w-0" data-id="element-250" data-mp="14h3bytq00pvo">
          <p className="text-[15px] font-semibold text-ink" data-id="element-251" data-mp="14fs70qkthc2z">
            {pct >= 1 ? "Daily goal done." : `${dailyGoal - xpToday} XP to your goal`}
          </p>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-ink-2" data-id="element-252" data-mp="uhulwby97vel">
            <FlameIcon className="h-4 w-4 text-bad" strokeWidth={2.5} aria-hidden="true" data-id="element-253" data-mp="qgipnnkvkbxf" />
            <span ref={ref} className="font-bold text-ink" data-id="element-254" data-mp="lhfm67kjmdrk">
              {value}
            </span>
            -day streak
          </p>
        </div>
      </div>
      <ol className="mt-5 grid grid-cols-7 gap-1.5" aria-label="Activity this week" data-id="element-255" data-mp="2ku1lmsz9bmgm">
        {week.map((d, i) => <li key={i} className="flex flex-col items-center gap-1" data-id="element-256" data-mp="2ftdve9iur9ng" data-mpi={i}>
            <span className={`grid h-7 w-full place-items-center rounded-lg ${d.active ? "bg-peach" : "bg-sunken"} ${i === 6 ? "ring-2 ring-brand/60 ring-offset-2 ring-offset-surface" : ""}`} aria-label={d.active ? "Practised" : "No practice"} data-id="element-257" data-mp="2ne6m7wor3pfv">
              {d.active && <FlameIcon className="h-3.5 w-3.5 text-bad" strokeWidth={2.5} aria-hidden="true" data-id="element-258" data-mp="325yop83g5906" />}
            </span>
            <span className="text-[11px] font-semibold text-ink-2" data-id="element-259" data-mp="29f6z4tgvzilt">{d.label}</span>
          </li>)}
      </ol>
    </Card>;
}